import React from 'react';
import { Filters } from './filters';
import { CountryTable } from 'models/Country';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { useUsers } from 'pages/admin/users/useUsers';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, Form, FormInstance, Image, Input, PageTitle, Row, Table, Tooltip, Typography } from 'components';
import { FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import styles from './styles.module.scss';
import { RolesDrawer } from 'pages/admin/users/rolesDrawer';
import { UserStatus } from 'components/common/userStatus';

const { Search } = Input;

export interface UserDataType {
  id: string;
  uuid: string | number;
  name: string;
  surname: string;
  email: string;
  department: string;
  rowTitle: string;
  role: string;
  country: CountryTable;
  languages: string[];
  createDate: string;
  lastModifiedDate: string;
}

export const Users: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    onAddUser,
    applyFilters,
    resetFilters,
    assignedUsersData,
    onExpand,
    drawerState,
    onRedirectToRole,
  } = useUsers();

  const columns: ColumnsType<UserDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Surname',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Assigned Roles',
      dataIndex: 'roles',
      key: 'role',
      render: (roles) => {
        if (roles?.length) {
          return (
            <Row align={'middle'} wrap={false} justify={'space-between'}>
              <Row className={styles.roles_column} wrap={false}>
                {!!roles?.length && (
                  <Tooltip title={roles[0]?.name}>
                    <Typography.Text
                      underline
                      type={'secondary'}
                      style={{ cursor: 'pointer', marginRight: 4 }}
                      onClick={() => {
                        onRedirectToRole(roles[0].id);
                      }}
                    >
                      {roles[0]?.name}
                    </Typography.Text>
                  </Tooltip>
                )}
              </Row>
              {roles?.length > 1 && (
                <Button
                  size={'small'}
                  type={'text'}
                  variant={'text'}
                  style={{ fontSize: '14px', color: '#525866', backgroundColor: 'transparent', fontWeight: 400 }}
                  onClick={() => onExpand(roles)}
                >
                  <Typography.Text underline type={'secondary'}>
                    +{roles?.length - 1}
                  </Typography.Text>
                </Button>
              )}
            </Row>
          );
        }
        return '';
      },
      minWidth: 150,
      sorter: false,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: false,
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.flagLocation && (
              <Image
                src={country?.flagLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Languages',
      dataIndex: 'languagesData',
      key: 'languages',
      render: (value) => {
        if (value?.length) {
          return (
            <Tooltip title={value.map((_language: any) => _language?.name).join(', ')}>
              {value[0]?.name}
              {value?.length > 1 ? <Typography.Text underline> +{value?.length - 1}</Typography.Text> : ''}
            </Tooltip>
          );
        }
        return '';
      },
      minWidth: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      minWidth: 100,
      sorter: true,
      render: (status) => <UserStatus status={status} />,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreateDate',
      key: 'createDate',
      minWidth: 150,
      sorter: true,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      minWidth: 150,
      sorter: false,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Users Management'} />
            <HasAccess role={PERMISSIONS.users.user.write}>
              <Button type={'primary'} onClick={onAddUser} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                tableLayout={'auto'}
                bordered
                scroll={{
                  x: true,
                }}
                rowKey="id"
                size={'small'}
                dataSource={data}
                columns={columns}
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                onChange={handleTableChange}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  size: 'default',
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                }}
              ></Table>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <RolesDrawer drawerState={drawerState} onExpand={onExpand} data={assignedUsersData} />
    </Col>
  );
};
